import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { BENEFITS, STATS } from "../data";

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    let start = null;
    const dur = 1300;
    const step = (t) => {
      if (start === null) start = t;
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
      else setVal(to);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
};

export const WhyUs = () => {
  return (
    <section id="dlaczego" data-testid="why-us-section" className="bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <span className="font-anton uppercase text-[#DC2626] tracking-widest text-sm">
            Dlaczego my
          </span>
          <h2 className="font-anton uppercase text-[#09090B] text-4xl md:text-5xl mt-3 leading-tight">
            Dlaczego warto wybrać ONE WAY?
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.label}
                data-testid={`benefit-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group flex items-center gap-4 bg-white border-2 border-[#09090B] p-5 hover:-translate-y-1.5 hover:border-[#DC2626] hover:shadow-[8px_8px_0_0_rgba(220,38,38,1)] transition-all duration-200"
              >
                <span className="w-12 h-12 shrink-0 bg-[#DC2626] flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                  <Icon className="text-white" size={24} strokeWidth={2.2} />
                </span>
                <span className="font-anton uppercase text-[#09090B] text-lg leading-tight">
                  {b.label}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Stats band */}
      <div data-testid="stats-band" className="bg-[#09090B]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="text-center px-4 py-6 sm:py-0"
            >
              <div className="font-anton text-[#DC2626] text-4xl md:text-6xl leading-none">
                {typeof s.value === "number" ? (
                  <Counter to={s.value} suffix={s.suffix} />
                ) : (
                  s.text
                )}
              </div>
              <div className="mt-3 font-anton uppercase text-white text-sm md:text-base tracking-wide">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
